import React, { useEffect, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import MenuItem from "@mui/material/MenuItem";
import { TimeZones } from "src/utils/constants";
import { EditProfileApi, memberDetail } from "src/DAL/Profile/Profile";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import countryList from "react-select-country-list";
import { s3baseUrl } from "src/config/config";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { gr } from "src/design-system/tokens";
import GrPage from "src/design-system/components/GrPage";
import GrLoader from "src/design-system/components/GrLoader";
import icons from "src/design-system/icons";
import GrAvatar from "src/design-system/components/GrAvatar";
import GrIcon from "src/design-system/components/GrIcon";
import GrButton from "src/design-system/components/GrButton";
import GrCard from "src/design-system/components/GrCard";
import GrCardHeader from "src/design-system/components/GrCardHeader";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrSelect from "src/design-system/components/GrSelect";
import GrText from "src/design-system/components/GrText";
import GrTextField from "src/design-system/components/GrTextField";
import GrPhoneField from "src/design-system/components/GrPhoneField";

const Input = styled("input")({
  display: "none",
});

TimeZones.sort();

// Two columns from tablet up, one column on phones.
const gridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    md: "repeat(2, minmax(0, 1fr))",
  },
  gap: gr.space[4],
};
const fullRowSx = { gridColumn: "1 / -1" };

// Hours / minutes / seconds boxes inside the Duration field: no own border, padding or focus ring.
const durationPartSx = {
  width: "20%",
  "&& .MuiOutlinedInput-root": { background: "transparent", boxShadow: "none" },
  "&& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&& .MuiInputBase-input": { textAlign: "center", padding: 0 },
};

// The Duration field shows the focus ring while any of its parts has focus.
const durationFieldSx = {
  "& > .MuiOutlinedInput-root:focus-within": { boxShadow: gr.shadow.focus },
  "& > .MuiOutlinedInput-root:focus-within > .MuiOutlinedInput-notchedOutline":
    {
      borderColor: gr.color.brand,
    },
};

const durationColonSx = {
  fontSize: gr.font.size.lg,
  fontWeight: gr.font.weight.semibold,
  color: gr.color.ink2,
};

// In the dashboard "Update Profile" popup the sections sit flat on the dialog; on the page they are cards.
function ProfileSection({ plain, children }) {
  if (plain) return <Box component="section">{children}</Box>;
  return <GrCard>{children}</GrCard>;
}

function EditProfile({ isOpenInfo, setIsOpenInfo = () => {} }) {
  const { enqueueSnackbar } = useSnackbar();
  const { setUserInfo } = useContentSetting();
  const [previews, setPreviews] = useState("");
  const [checkedPace, setCheckedPace] = useState(true);
  const [checked, setChecked] = useState(false);
  const [memberData, setMemberData] = useState({
    hours: 1,
    minutes: 0,
    seconds: 0,
    distance: "5km",
    gender: "male",
    weight: "",
    height: "",
    age: "",
    activity_level: "sedentary",
    deficit_percentage: true,
    deficit_value: "",
    days_for_deficit: "",
  });
  const [memberVdotData, setMemberVdotDataData] = useState({
    time_interval: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [timeZoneValue, setTimeZoneValue] = useState("Europe/Dublin");
  const [inputValue, setInputValue] = React.useState("");
  const [image, setImage] = useState("");
  const options = useMemo(() => countryList().getData(), []);

  const handleChangeDate = (event) => {
    setMemberData((prevState) => {
      return {
        ...prevState,
        dob: event?.$d,
      };
    });
  };

  const handleSelectOther = (event) => {
    setMemberData((prevState) => {
      return {
        ...prevState,
        last_cycle_date: event?.$d,
      };
    });
  };

  const handleChangePace = (event) => {
    setCheckedPace(event.target.checked);
    setMemberVdotDataData((inputs) => ({
      ...inputs,
      ["time_interval"]: 0,
    }));
    setMemberData((inputs) => ({
      ...inputs,
      ["distance"]: "5km",
    }));
  };

  const handleChangeCheck = (event) => {
    setChecked(event.target.checked);
  };

  const getMemberDetail = async () => {
    const result = await memberDetail();
    if (result.code === 200) {
      setMemberVdotDataData((inputs) => ({
        ...inputs,
        ["time_interval"]: parseInt(
          result?.member?.vdot?.time_taken
            ? result?.member?.vdot?.time_taken
            : 0
        ),
      }));
      setCheckedPace(result?.member?.pace_calculator);
      setMemberData((inputs) => ({
        ...inputs,
        ...result.member,
        ["distance"]: result?.member?.vdot?.race_type,
      }));
      setChecked(result?.member?.permission_cycle || false);
      setTimeZoneValue(result.member.time_zone);
      setCheckedPace(result?.member?.pace_calculator);
      setIsLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  const calculateTDEE = (value) => {
    const { gender, weight, height, age, activity_level } = value;

    if (!gender || !weight || !height || !age || !activity_level) {
      return 0;
    }

    let BMR;

    if (gender === "male") {
      BMR = 10 * weight + 6.25 * height - 5 * age + 5;
    } else if (gender === "female") {
      BMR = 10 * weight + 6.25 * height - 5 * age - 161;
    } else {
      BMR = 10 * weight + 6.25 * height - 5 * age - 78;
    }

    const activityMultipliers = {
      sedentary: 1.2,
      lightly: 1.375,
      moderately: 1.55,
      active: 1.725,
      super: 1.9,
    };

    const TDEE = BMR * (activityMultipliers[activity_level] || 1.2);

    return Math.round(TDEE);
  };

  let totalKcal = calculateTDEE(memberData);

  const handleUpdate = async (e) => {
    e.preventDefault();
    var dateString = moment(memberData.dob).format("YYYY-MM-DD");
    let [year, month, day] = [1, 1, 1];

    if (dateString !== "Invalid date") {
      [year, month, day] = dateString.split("-");
      if (year < 1900 || year > moment().year()) {
        enqueueSnackbar("Please select a valid year", { variant: "error" });
      } else {
        let VDOT = {
          race_type: memberData?.distance,
          time_taken: checkedPace ? memberVdotData?.time_interval : 0,
        };
        const formData = new FormData();
        var dateString = moment(memberData.dob).format("YYYY-MM-DD");
        const momentDate = moment(memberData?.last_cycle_date);
        const dateString1 = momentDate.isValid()
          ? momentDate.format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD");

        formData.append("first_name", memberData.first_name);
        formData.append("last_name", memberData.last_name);
        formData.append("street", memberData.street);
        formData.append("city", memberData.city);
        formData.append("zip_code", memberData.zip_code);
        formData.append("state", memberData.state);
        formData.append("country", memberData.country);
        formData.append("time_zone", timeZoneValue);
        formData.append("member_biography", memberData.member_biography);
        formData.append("contact_number", memberData.contact_number);
        formData.append("pace_calculator", checkedPace);
        formData.append("gender", memberData.gender);
        formData.append("age", memberData.age);
        formData.append("weight", memberData.weight);
        formData.append("height", memberData.height);
        formData.append("activity_level", memberData.activity_level);
        formData.append("deficit_percentage", memberData.deficit_percentage);
        formData.append("deficit_value", memberData.deficit_value || 0);
        formData.append("days_for_deficit", memberData.days_for_deficit || 0);
        formData.append("regular_day_calories", totalKcal);
        formData.append("permission_cycle", checked);

        formData.append("vdot", JSON.stringify(VDOT));

        formData.append("dob", dateString);
        formData.append("last_cycle_date", dateString1);
        if (image) {
          formData.append("profile_image", image);
        }

        if (memberData.deficit_percentage) {
          if (
            memberData.days_for_deficit > 30 ||
            memberData.days_for_deficit < 1
          ) {
            enqueueSnackbar("Deficit Duration must be between 1-30", {
              variant: "error",
            });
            return;
          }
          if (memberData.deficit_value > 30 || memberData.deficit_value < 1) {
            enqueueSnackbar("Deficit Percentage must be between 1-30", {
              variant: "error",
            });
            return;
          }
        }

        if (checkedPace && memberVdotData?.time_interval == 0) {
          return enqueueSnackbar(
            "Please enter a value in the Duration field.",
            {
              variant: "error",
            }
          );
        }

        if (!memberData.weight || Number(memberData.weight) <= 0) {
          enqueueSnackbar("Weight is required and must be greater than 0", {
            variant: "error",
          });
          return;
        }

        if (!memberData.height || Number(memberData.height) <= 0) {
          enqueueSnackbar("Height is required and must be greater than 0", {
            variant: "error",
          });
          return;
        }

        if (!memberData.age || Number(memberData.age) <= 0) {
          enqueueSnackbar("Age is required and must be greater than 0", {
            variant: "error",
          });
          return;
        }

        setIsLoadingForm(true);
        const result = await EditProfileApi(formData);
        if (result.code === 200) {
          localStorage.setItem(`user_data`, JSON.stringify(result?.member));
          localStorage.setItem(`user_data_new`, JSON.stringify(result?.member));
          // Refresh the top bar (avatar, name) with the saved profile.
          setUserInfo((values) => ({ ...values, ...result?.member }));
          enqueueSnackbar(result.message, { variant: "success" });
          setIsLoadingForm(false);
          setIsOpenInfo(false);
        } else {
          enqueueSnackbar(result.message, { variant: "error" });
          setIsLoadingForm(false);
        }
      }
    } else {
      enqueueSnackbar("Please select a valid date", { variant: "error" });
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setMemberData((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const handleUpload = (event) => {
    setImage(event.target.files[0]);
    const preview = URL.createObjectURL(event.target.files[0]);
    setPreviews(preview);
  };

  const handleHoursChange = (e) => {
    const hours = parseInt(e.target.value) || 0;
    const minutes = Math.floor((memberVdotData.time_interval % 3600) / 60);
    const seconds = memberVdotData.time_interval % 60;
    updateIntervalTime(hours * 3600 + minutes * 60 + seconds);
  };

  const handleMinutesChange = (e) => {
    let minutes = parseInt(e.target.value) || 0;
    minutes = Math.min(Math.max(minutes, 0), 59);

    // Calculate hours separately to prevent resetting
    const currentHours = Math.floor(memberVdotData.time_interval / 3600);
    const seconds = memberVdotData.time_interval % 60;

    // Update total seconds with new minutes and current hours
    updateIntervalTime(currentHours * 3600 + minutes * 60 + seconds);
  };

  const handleSecondsChange = (e) => {
    let seconds = parseInt(e.target.value) || 0;
    seconds = Math.min(Math.max(seconds, 0), 59); // Ensure the value is between 0 and 60
    const minutes = Math.floor(memberVdotData.time_interval / 60);
    updateIntervalTime(minutes * 60 + seconds);
  };

  const updateIntervalTime = (totalSeconds) => {
    setMemberVdotDataData((prevState) => {
      return {
        ...prevState,
        ["time_interval"]: totalSeconds,
      };
    });
  };

  const formatWithLeadingZeros = (value) => {
    return value < 10 ? `0${value}` : `${value}`;
  };

  const today = new Date();
  const startDate = memberData?.deficit_start_date
    ? new Date(memberData.deficit_start_date)
    : today;

  if (memberData.days_for_deficit > 0 && memberData.days_for_deficit < 31) {
    startDate.setDate(
      startDate.getDate() + parseInt(memberData.days_for_deficit)
    );
  }

  const formattedDate = startDate.toISOString().split("T")[0];

  useEffect(() => {
    getMemberDetail();
  }, []);

  if (isLoading) {
    return (
      <GrLoader sx={{ py: gr.space[12] }} />
    );
  }

  const headingLevel = isOpenInfo ? "h3" : "h2";

  // One wrapper type always (GrPage), so the form does not remount when the info popup closes;
  // inside the popup its padding is the popup's own, as before.
  return (
    <GrPage
      sx={
        isOpenInfo
          ? { px: { xs: gr.space[4], sm: gr.space[6] }, pt: 0, pb: gr.space[6] }
          : { pb: gr.space[8] }
      }
    >
      {!isOpenInfo && <GrPageHeader title="Edit Profile" />}
      <form onSubmit={handleUpdate}>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: gr.space[6] }}
        >
          {isOpenInfo && (
            <GrText variant="body" sx={{ color: gr.color.ink2 }}>
              Please fill in all required fields to proceed to the portal. You
              can update this information later from the Edit Profile section.
            </GrText>
          )}

          <ProfileSection plain={isOpenInfo}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: gr.space[6],
              }}
            >
              <Box sx={{ position: "relative", width: 100, height: 100 }}>
                <GrAvatar
                  src={
                    previews ||
                    (memberData.profile_image
                      ? s3baseUrl + memberData.profile_image
                      : undefined)
                  }
                  name={`${memberData.first_name ?? ""} ${
                    memberData.last_name ?? ""
                  }`}
                  size={100}
                  sx={{ border: `1px solid ${gr.color.line}` }}
                />
                <label htmlFor="icon-button-file">
                  <Input
                    accept="image/*"
                    id="icon-button-file"
                    type="file"
                    name="profile_image"
                    onChange={(e) => handleUpload(e)}
                  />
                  <GrIconButton
                    component="span"
                    size="sm"
                    aria-label="upload picture"
                    sx={{
                      position: "absolute",
                      right: 0,
                      bottom: 0,
                      borderRadius: gr.radius.pill,
                      boxShadow: gr.shadow.raised,
                    }}
                  >
                    <GrIcon icon={icons.camera} size="sm" />
                  </GrIconButton>
                </label>
              </Box>
            </Box>

            <Box sx={gridSx}>
              <GrTextField
                id="profile-first_name"
                label="First Name"
                name="first_name"
                value={memberData.first_name}
                onChange={(e) => handleChange(e)}
                required={true}
              />
              <GrTextField
                id="profile-last_name"
                label="Last Name"
                value={memberData.last_name}
                name="last_name"
                onChange={(e) => handleChange(e)}
                required={true}
              />
              <GrTextField
                id="profile-email"
                label="Email"
                readOnly={true}
                type="email"
                inputProps={{ readOnly: true }}
                value={memberData.email}
                name="email"
                onChange={(e) => handleChange(e)}
                required={true}
              />
              <GrPhoneField
                id="profile-contact_number"
                label="Phone"
                value={memberData.contact_number}
                name="contact_number"
                defaultCountry={memberData.country}
                onChange={(e) => handleChange(e)}
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date of birth"
                  name="dob"
                  className="mui-date-picker"
                  value={memberData.dob}
                  format="DD/MM/YYYY"
                  mask="__/__/____"
                  inputFormat="DD/MM/YYYY"
                  onChange={(e) => handleChangeDate(e)}
                  renderInput={(params) => (
                    <GrTextField {...params} required={true} />
                  )}
                />
              </LocalizationProvider>
              <GrTextField
                id="profile-street"
                label="Street"
                value={memberData.street}
                name="street"
                onChange={(e) => handleChange(e)}
                required={true}
              />
              <GrTextField
                id="profile-state"
                label="State"
                value={memberData.state}
                name="state"
                onChange={(e) => handleChange(e)}
                required={true}
              />
              <GrTextField
                id="profile-city"
                label="Town or City"
                value={memberData.city}
                name="city"
                onChange={(e) => handleChange(e)}
                required={true}
              />
              <GrSelect
                id="profile-country"
                value={memberData.country}
                name="country"
                label="Country"
                onChange={handleChange}
                required
              >
                {options.map((option_value) => {
                  return (
                    <MenuItem
                      key={option_value.value}
                      value={option_value.value}
                    >
                      {option_value.label}
                    </MenuItem>
                  );
                })}
              </GrSelect>
              <GrTextField
                id="profile-zip_code"
                label="Area Code"
                value={memberData.zip_code}
                name="zip_code"
                onChange={(e) => handleChange(e)}
                required={true}
              />
              <Autocomplete
                value={timeZoneValue}
                onChange={(event, newValue) => {
                  setTimeZoneValue(newValue);
                }}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                id="profile-time_zone"
                options={TimeZones}
                renderInput={(params) => (
                  <GrTextField {...params} label="Time Zone *" />
                )}
              />
              <GrSelect
                id="profile-gender"
                name="gender"
                value={memberData.gender}
                label="Gender"
                onChange={handleChange}
                required
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </GrSelect>
              {memberData.gender === "female" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: gr.space[2],
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Last Cycle Date"
                        name="last_cycle_date"
                        className="mui-date-picker"
                        value={memberData.last_cycle_date || null}
                        format="DD/MM/YYYY"
                        mask="__/__/____"
                        inputFormat="DD/MM/YYYY"
                        onChange={(e) => handleSelectOther(e)}
                        renderInput={(params) => (
                          <GrTextField {...params} required={true} />
                        )}
                      />
                    </LocalizationProvider>
                  </Box>
                  <Switch
                    checked={checked}
                    onChange={handleChangeCheck}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                  <Tooltip
                    title="To help your coach tailor your training plan more effectively, you can choose to share your menstrual cycle date. Simply turn ON the switch if you wish to share this information with your coach. "
                    placement="top"
                  >
                    <Box
                      component="span"
                      tabIndex={0}
                      sx={{
                        display: "inline-flex",
                        flex: "none",
                        color: gr.color.brand,
                        cursor: "pointer",
                        borderRadius: gr.radius.pill,
                        "&:focus-visible": {
                          outline: "none",
                          boxShadow: gr.shadow.focus,
                        },
                      }}
                    >
                      <GrIcon icon={icons.info} size="lg" />
                    </Box>
                  </Tooltip>
                </Box>
              )}
              <GrTextField
                id="profile-weight"
                value={memberData?.weight}
                onChange={handleChange}
                name="weight"
                type="number"
                label="Weight"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">kg</InputAdornment>
                  ),
                }}
              />
              <GrTextField
                id="profile-height"
                value={memberData?.height}
                type="number"
                onChange={handleChange}
                name="height"
                label="Height"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">cm</InputAdornment>
                  ),
                }}
              />
              <GrTextField
                id="profile-age"
                value={memberData?.age}
                type="number"
                onChange={handleChange}
                name="age"
                label="Age"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">years</InputAdornment>
                  ),
                }}
              />
              <GrSelect
                id="profile-activity_level"
                name="activity_level"
                value={memberData.activity_level}
                label="Activity Level"
                onChange={handleChange}
                required
              >
                <MenuItem value="sedentary">
                  Sedentary (little/no exercise)
                </MenuItem>
                <MenuItem value="lightly">
                  Lightly active (1-3 days/week exercise)
                </MenuItem>
                <MenuItem value="moderately">
                  Moderately active (3-5 days/week exercise)
                </MenuItem>
                <MenuItem value="active">
                  Very active (6-7 days/week exercise)
                </MenuItem>
                <MenuItem value="super">
                  Super active (athlete or hard labor)
                </MenuItem>
              </GrSelect>
              <GrTextField
                id="profile-member_biography"
                label="Biography"
                rows={4}
                multiline
                value={memberData.member_biography}
                name="member_biography"
                onChange={(e) => handleChange(e)}
                sx={fullRowSx}
              />
            </Box>
          </ProfileSection>

          <ProfileSection plain={isOpenInfo}>
            <GrCardHeader
              as={headingLevel}
              title="Calorie Deficit"
              subtitle={
                memberData.deficit_percentage
                  ? "Enter a calorie deficit percentage between 1-30% and set the duration between 1-30 days."
                  : null
              }
            />
            <Box sx={gridSx}>
              <GrSelect
                id="profile-deficit_percentage"
                name="deficit_percentage"
                value={memberData.deficit_percentage}
                label="Is Allowed Calorie Deficit"
                onChange={handleChange}
                required
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </GrSelect>
              {memberData.deficit_percentage && (
                <>
                  <GrTextField
                    id="profile-deficit_value"
                    value={memberData?.deficit_value}
                    type="number"
                    onChange={handleChange}
                    name="deficit_value"
                    label="Calorie Deficit Percentage"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                  <GrTextField
                    id="profile-days_for_deficit"
                    label="Calorie Deficit Duration (Days)"
                    type="number"
                    required
                    name="days_for_deficit"
                    onChange={handleChange}
                    value={memberData.days_for_deficit}
                  />
                  {memberData.days_for_deficit > 0 &&
                    memberData.days_for_deficit < 31 && (
                      <GrText
                        variant="secondary"
                        sx={{
                          ...fullRowSx,
                          textAlign: { xs: "left", sm: "right" },
                        }}
                      >
                        Calorie Deficit Period:{" "}
                        {moment(
                          memberData?.deficit_start_date || new Date()
                        ).format("DD-MM-YYYY")}{" "}
                        to {moment(formattedDate).format("DD-MM-YYYY")}
                      </GrText>
                    )}
                </>
              )}
            </Box>
          </ProfileSection>

          <ProfileSection plain={isOpenInfo}>
            <GrCardHeader
              as={headingLevel}
              title="Pace Calculator"
              subtitle={
                checkedPace ? "Enter results to calculate the pace." : null
              }
              action={
                <Switch
                  checked={checkedPace}
                  onChange={handleChangePace}
                  inputProps={{ "aria-label": "Pace Calculator" }}
                />
              }
              sx={checkedPace ? undefined : { mb: 0 }}
            />
            {checkedPace && (
              <Box sx={gridSx}>
                <GrSelect
                  id="profile-distance"
                  value={memberData.distance}
                  name="distance"
                  label="Distance"
                  onChange={handleChange}
                >
                  <MenuItem value="5km">5 (km)</MenuItem>
                  <MenuItem value="10km">10 (km)</MenuItem>
                  <MenuItem value="15km">15 (km)</MenuItem>
                  <MenuItem value="half_marathon">Half Marathon</MenuItem>
                  <MenuItem value="marathon">Marathon</MenuItem>
                </GrSelect>
                <GrTextField
                  id="profile-duration"
                  label="Duration (hh:mm:ss)"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <TextField
                          id="outlined-basic-hours"
                          type="number"
                          size="small"
                          placeholder="Hrs"
                          value={formatWithLeadingZeros(
                            Math.floor(memberVdotData.time_interval / 3600)
                          )}
                          onChange={handleHoursChange}
                          sx={durationPartSx}
                        />
                        <Box component="span" sx={durationColonSx}>
                          :
                        </Box>
                        <TextField
                          id="outlined-basic-minutes"
                          type="number"
                          size="small"
                          placeholder="Min"
                          value={formatWithLeadingZeros(
                            Math.floor(
                              (memberVdotData.time_interval % 3600) / 60
                            )
                          )}
                          onChange={handleMinutesChange}
                          sx={durationPartSx}
                        />
                        <Box component="span" sx={durationColonSx}>
                          :
                        </Box>
                        <TextField
                          id="outlined-basic-seconds"
                          type="number"
                          size="small"
                          placeholder="Sec"
                          value={formatWithLeadingZeros(
                            memberVdotData.time_interval % 60
                          )}
                          onChange={handleSecondsChange}
                          sx={durationPartSx}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={durationFieldSx}
                />
              </Box>
            )}
          </ProfileSection>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <GrButton type="submit" sx={{ width: { xs: "100%", sm: "auto" } }}>
              {isLoadingForm ? "Updating..." : "Update"}
            </GrButton>
          </Box>
        </Box>
      </form>
    </GrPage>
  );
}

export default EditProfile;
